import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Mic, MicOff, Video, VideoOff, Send, Users, Utensils, Subtitles, PhoneOff } from "lucide-react";
import { TranscriptionService, saveTranscript } from "@/services/webrtc/TranscriptionService";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import LateNightSnackPanel, { SnackSpot, SnackSpotDraft } from "./LateNightSnackPanel";
import { AIAssistantPanel } from "./AIAssistantPanel";
import { SnackOrderTracker, ActiveOrder } from "./SnackOrderTracker";
import { OrderETAModal } from "./OrderETAModal";

/**
 * MobileVirtualRoomKeyboardAdaptive - Mobile-optimized video conferencing
 * 
 * Features:
 * - Keyboard-adaptive two-column layout (videos left, chat right)
 * - Active speaker detection with auto-switching
 * - AI closed captioning with transcription
 * - Full-width input above keyboard
 * - Independent scrolling for participants and chat
 */

export type MobileVirtualRoomParticipant = {
  id: string;
  name: string;
  role: string;
  initials: string;
  online: boolean;
};

export interface MobileVirtualRoomProps {
  tabLabel: string;
  roomTitle: string;
  description?: string;
  classId?: string;
  tabId?: string;
  roomId?: string;
  participants?: MobileVirtualRoomParticipant[];
  onClose: () => void;
  // Snack props
  snackSpots?: SnackSpot[];
  snackLoading?: boolean;
  onCreateSnack?: (spot: SnackSpotDraft) => Promise<void>;
  onUpdateSnack?: (id: number, updates: Partial<SnackSpot>) => Promise<void>;
  onDeleteSnack?: (id: number) => Promise<void>;
  onMarkSnackOrdered?: (id: number) => Promise<void>;
  agenda?: string[]; // Agenda items from parent
  onMinimize?: () => void; // Callback to minimize to floating PiP
  minimized?: boolean; // Whether room is minimized to PiP
}

interface ChatMessage {
  id: string;
  sender: string;
  senderInitials: string;
  text: string;
  timestamp: number;
}

const MobileVirtualRoomNew: React.FC<MobileVirtualRoomProps> = ({
  tabLabel,
  roomTitle,
  description,
  classId = '',
  tabId = '',
  roomId,
  participants: initialParticipants = [],
  onClose,
  snackSpots = [],
  snackLoading = false,
  onCreateSnack,
  onUpdateSnack,
  onDeleteSnack,
  onMarkSnackOrdered,
  agenda = [], // Default to empty agenda
  onMinimize,
  minimized = false,
}) => {
  console.log('[MobileVirtualRoomNew] Component rendering with props:', { tabLabel, roomTitle, classId, tabId });
  
  // Auth
  const { user } = useAuth();
  console.log('[MobileVirtualRoomNew] User:', user?.id);
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'You';
  const userInitials = displayName?.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) || 'YO';
  console.log('[MobileVirtualRoomNew] Display name:', displayName, 'Initials:', userInitials);
  
  // Call State
  const [onCall, setOnCall] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  
  // Transcription State (from desktop)
  const [transcriptionService] = useState(() => new TranscriptionService());
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptText, setTranscriptText] = useState<string>("");
  const [transcriptInterval, setTranscriptInterval] = useState<NodeJS.Timeout | null>(null);
  const [recognition, setRecognition] = useState<any>(null);
  const [lastTranscript, setLastTranscript] = useState<any>(null);

  // AI Assistant State
  const [aiChatHistory, setAiChatHistory] = useState<{ question: string; answer: string }[]>([]);
  const [aiEnabled, setAiEnabled] = useState(true);
  
  // Reset function for new meetings
  const resetMeeting = useCallback(() => {
    setMessages([]);
    setAiChatHistory([]);
    setInputValue('');
    setTranscriptText('');
    setIsTranscribing(false);
    console.log('[MobileVirtualRoomNew] Meeting reset - cleared all data');
  }, []);
  const [aiAssistant] = useState({
    sessionDuration: 0,
    summary: "AI summary will appear here as you speak...",
    keyPoints: [] as string[],
    actionItems: [] as { owner: string; task: string; deadline?: string }[],
    questions: [] as { text: string; askedBy: string }[],
  });

  // Snack Panel State
  const [showSnackPanel, setShowSnackPanel] = useState(false);

  // Agenda/AI Toggle State
  const [showAgenda, setShowAgenda] = useState(true);

  // Snack Order Tracking State
  const [activeOrders, setActiveOrders] = useState<ActiveOrder[]>([]);
  const [showETAModal, setShowETAModal] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<{ restaurant: string; item: string; url?: string } | null>(null);
  const [callSpotName, setCallSpotName] = useState('');

  // Keyboard Detection
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  // Side Tab State
  const [showSideTab, setShowSideTab] = useState(false);

  // Confirmation Dialog
  const [showEndCallConfirm, setShowEndCallConfirm] = useState(false);

  // Chat State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Participants State
  const [participants, setParticipants] = useState<MobileVirtualRoomParticipant[]>(initialParticipants);
  const [activeSpeaker, setActiveSpeaker] = useState<MobileVirtualRoomParticipant | null>(null);
  const participantsRef = useRef<HTMLDivElement>(null);

  // WebRTC State (commented out for now)
  // const [webrtcManager] = useState(() => new WebRTCRoomManager());
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);

  // AI Response Generation Function - Full AI with Web Search
  const generateAIResponse = async (question: string, context: string): Promise<string> => {
    try {
      // Build conversation history for context
      const conversationHistory = aiChatHistory.map(qa => [
        { role: 'user', content: qa.question },
        { role: 'assistant', content: qa.answer }
      ]).flat();
      
      // Add meeting context to system message
      const systemContext = `You are a helpful AI assistant in a virtual meeting room. 
Meeting Context: ${context}
Room: ${roomTitle}
Participants: ${participants.map(p => p.name).join(', ')}
Current Time: ${new Date().toLocaleTimeString()}

You can answer questions about:
1. The current meeting (participants, transcript, agenda)
2. General knowledge and web searches
3. Math, coding, explanations, and anything else the user asks

Be helpful, concise, and accurate.`;

      // Call Supabase Edge Function (same as desktop)
      const { data, error } = await supabase.functions.invoke('ai-assist', {
        body: {
          mode: 'chat',
          payload: {
            conversation: [
              { role: 'system', content: systemContext },
              ...conversationHistory,
              { role: 'user', content: question }
            ],
            webSearch: true, // Enable web search for any question
          },
        },
      });

      if (error) {
        console.error('[AI] Supabase function error:', error);
        throw error;
      }

      return data?.result?.reply || 'Sorry, I could not process that request.';
      
    } catch (error) {
      console.error('[AI] Error generating response:', error);
      
      // Fallback to basic responses if API fails
      const lowerQ = question.toLowerCase();
      if (lowerQ.includes('participant') || lowerQ.includes('who')) {
        return `Current participants: ${participants.map(p => p.name).join(', ')}. Total: ${participants.length} people.`;
      }
      if (lowerQ.includes('transcript') || lowerQ.includes('summary')) {
        return transcriptText ? `Here's what's been said: "${transcriptText.substring(0, 200)}..."` : 'No transcript available yet.';
      }
      if (lowerQ.includes('agenda')) {
        return agenda.length > 0 ? `Agenda: ${agenda.join(', ')}` : 'No agenda items set.';
      }
      
      return 'Sorry, I had trouble connecting to AI services. Please check your connection and try again.';
    }
  };

  // Connect stream to video element
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Add current user to participants if not already there
  useEffect(() => {
    const userExists = participants.some(p => p.id === user?.id);
    if (!userExists && user) {
      setParticipants(prev => [{
        id: user.id,
        name: displayName,
        initials: userInitials,
        role: 'host',
        online: true,
      }, ...prev]);
    }
  }, [user, displayName, userInitials]); // Removed participants from deps to avoid infinite loop

  // Set first participant as active speaker initially
  useEffect(() => {
    if (!activeSpeaker && participants.length > 0) {
      setActiveSpeaker(participants[0]);
    }
  }, [participants, activeSpeaker]);

  // Keyboard Detection - Visual Viewport API
  useEffect(() => {
    const checkKeyboard = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const isKeyboardOpen = viewportHeight < windowHeight * 0.75;
        setKeyboardVisible(isKeyboardOpen);
        console.log('[Mobile VR] Keyboard visible:', isKeyboardOpen);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', checkKeyboard);
      window.visualViewport.addEventListener('scroll', checkKeyboard);
      checkKeyboard(); // Initial check
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', checkKeyboard);
        window.visualViewport.removeEventListener('scroll', checkKeyboard);
      }
    };
  }, []);

  // Auto-scroll chat to bottom on new message
  useEffect(() => {
    if (chatRef.current && messages.length > 0) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Phone Call Detection (for snack ordering) - ONLY for external phone calls, not virtual room joins
  useEffect(() => {
    const checkCallStatus = () => {
      const isOnCall = sessionStorage.getItem('whipt_on_call') === 'true';
      const storedSpotName = sessionStorage.getItem('whipt_call_spot') || '';

      // Only process if we detect an EXTERNAL phone call (has a spot name)
      if (isOnCall && storedSpotName && !callSpotName) {
        // External phone call just started
        console.log('[Mobile VirtualRoom] External phone call started for:', storedSpotName);
        setCallSpotName(storedSpotName);

        // Force mic and camera off during phone call
        if (micOn) {
          setMicOn(false);
          if (localStream) {
            const audioTrack = localStream.getAudioTracks()[0];
            if (audioTrack) audioTrack.enabled = false;
          }
        }
        if (cameraOn) {
          setCameraOn(false);
          if (localStream) {
            const videoTrack = localStream.getVideoTracks()[0];
            if (videoTrack) videoTrack.enabled = false;
          }
        }

        // Add chat message
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString(),
            sender: displayName,
            senderInitials: userInitials,
            text: `📞 On a quick call - be right back!`,
            timestamp: Date.now(),
          },
        ]);
      } else if (!isOnCall && callSpotName) {
        // External phone call ended
        console.log('[Mobile VirtualRoom] External phone call ended');
        setCallSpotName('');

        // Add "back" message
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString(),
            sender: displayName,
            senderInitials: userInitials,
            text: `✅ Back from my call!`,
            timestamp: Date.now(),
          },
        ]);

        // Check if there's a pending order and show ETA modal
        if (pendingOrder) {
          console.log('[Mobile Snack] Showing ETA modal after call ended');
          setShowETAModal(true);
        }
      }
    };

    // DON'T check immediately - only start checking if user makes a snack order call
    // This prevents false "on a quick call" popup on component mount
    
    // Check periodically (every 500ms)
    const interval = setInterval(checkCallStatus, 500);

    return () => clearInterval(interval);
  }, [onCall, micOn, cameraOn, localStream, displayName, pendingOrder]);

  // WebRTC Setup
  useEffect(() => {
    const initWebRTC = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: cameraOn,
          audio: micOn,
        });
        
        setLocalStream(stream);
        
        if (localVideoRef.current && cameraOn) {
          localVideoRef.current.srcObject = stream;
        }

        console.log('[Mobile VR] WebRTC initialized');
      } catch (error) {
        console.error('[Mobile VR] Failed to get media:', error);
      }
    };

    if (onCall && (micOn || cameraOn)) {
      initWebRTC();
    }

    return () => {
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [onCall, micOn, cameraOn]);

  // Toggle transcription (copied from desktop)
  const toggleTranscription = useCallback(async () => {
    if (isTranscribing) {
      console.log('[MobileVR] Stopping transcription');
      
      // Stop Web Speech API
      if (recognition) {
        recognition.stop();
      }
      
      // Clear simulation interval
      if (transcriptInterval) {
        clearInterval(transcriptInterval);
        setTranscriptInterval(null);
      }
      
      const audioBlob = await transcriptionService.stopRecording();
      setIsTranscribing(false);
      
      if (audioBlob) {
        try {
          const result = await transcriptionService.transcribe(audioBlob);
          setLastTranscript(result);
          setTranscriptText(result.fullText || `✅ Captured ${(audioBlob.size / 1024).toFixed(1)}KB`);
          await saveTranscript(roomId || 'room', result, {
            roomTitle,
            participants: participants.map((p) => p.name),
            classId,
            categoryId: tabId,
            userId: user?.id,
          });
          setTimeout(() => setTranscriptText(result.fullText || ''), 2000);
        } catch (err) {
          console.error('[MobileVR] Transcription save error:', err);
          setTranscriptText('⚠️ Could not transcribe/save audio');
          setTimeout(() => setTranscriptText(''), 3000);
        }
      }
    } else {
      console.log('[MobileVR] Starting transcription');
      
      // Get or request audio stream
      let stream: MediaStream | null = null;
      
      if (micOn && localStream) {
        stream = localStream;
        console.log('[MobileVR] Using existing audio stream');
      }
      
      // If no stream, request microphone access
      if (!stream) {
        try {
          console.log('[MobileVR] Requesting microphone access...');
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          setMicOn(true);
          console.log('[MobileVR] ✅ Got microphone stream');
        } catch (error) {
          console.error('[MobileVR] Failed to access microphone:', error);
          alert('Need microphone access to record. Please enable your microphone first.');
          return;
        }
      }
      
      if (stream) {
        try {
          await transcriptionService.startRecording(stream);
          setIsTranscribing(true);
          
          // Use Web Speech API for real-time transcription
          const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
          
          if (SpeechRecognition) {
            const recognitionInstance = new SpeechRecognition();
            recognitionInstance.continuous = true;
            recognitionInstance.interimResults = true;
            recognitionInstance.lang = 'en-US';
            recognitionInstance.maxAlternatives = 1;
            
            console.log('[MobileVR] Web Speech API configured for continuous recognition');
            
            recognitionInstance.onresult = (event: any) => {
              let interimTranscript = '';
              let finalTranscript = '';
              
              for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                  finalTranscript += transcript + ' ';
                } else {
                  interimTranscript += transcript;
                }
              }
              
              const displayText = finalTranscript || interimTranscript;
              if (displayText) {
                setTranscriptText(displayText);

                // RELAY captions to remote peers via data channel (if WebRTC is connected)
                const manager = (window as any).__webrtcManager;
                if (manager && typeof manager.sendDataChannelMessage === 'function') {
                  const captionPayload = JSON.stringify({
                    type: 'caption',
                    text: displayText,
                    author: displayName,
                    timestamp: Date.now(),
                  });
                  manager.sendDataChannelMessage(captionPayload);
                }
              }
            };

            recognitionInstance.onerror = (event: any) => {
              console.error('[MobileVR] Speech recognition error:', event.error);
              if (event.error === 'no-speech') {
                setTranscriptText('🎤 Listening... (speak now)');
              } else if (event.error === 'not-allowed') {
                setTranscriptText('❌ Microphone access denied');
                setIsTranscribing(false);
              } else if (event.error === 'network') {
                setTranscriptText('⚠️ Network error - check connection');
              } else {
                setTranscriptText(`⚠️ Error: ${event.error}`);
              }
            };
            
            recognitionInstance.onstart = () => {
              setTranscriptText('🎤 Listening... Speak now!');
            };
            
            recognitionInstance.onend = () => {
              if (isTranscribing) {
                recognitionInstance.start();
              }
            };
            
            setRecognition(recognitionInstance);
            recognitionInstance.start();
            console.log('[MobileVR] ✅ Real-time speech recognition started');
          } else {
            // Fallback to demo mode
            console.warn('[MobileVR] Web Speech API not available');
            const demoSentences = [
              "🎙️ Recording in progress...",
              "This is a demonstration of live captions.",
              "Your actual speech would appear here in real-time",
              "with AI speech-to-text transcription.",
              "The system is recording your audio.",
              "Stop recording to save the transcript."
            ];
            
            let sentenceIndex = 0;
            setTranscriptText(demoSentences[0]);
            
            const interval = setInterval(() => {
              sentenceIndex++;
              if (sentenceIndex >= demoSentences.length) {
                sentenceIndex = 0;
              }
              setTranscriptText(demoSentences[sentenceIndex]);
            }, 3000);
            
            setTranscriptInterval(interval);
          }
        } catch (error) {
          console.error('[MobileVR] Failed to start transcription:', error);
          alert('Failed to start transcription. Please try again.');
        }
      }
    }
  }, [isTranscribing, recognition, transcriptInterval, transcriptionService, micOn, localStream, roomId, roomTitle, participants, classId, tabId, user?.id]);

  // Handle sending messages
  const handleSendMessage = useCallback(() => {
    if (!inputValue.trim()) return;

    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      sender: displayName,
      senderInitials: userInitials,
      text: inputValue.trim(),
      timestamp: Date.now(),
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    
    // TODO: Send via WebRTC data channel
    console.log('[Mobile VR] Sent message:', newMessage);
  }, [inputValue, displayName, userInitials]);

  // Handle input key press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle closing room
  const confirmAndClose = useCallback(async () => {
    // Save transcript if exists
    if (isTranscribing && transcriptText.trim()) {
      const shouldSave = window.confirm('Save transcript before leaving?');
      if (shouldSave) {
        const title = prompt('Transcript name:', `${tabLabel} Notes - ${new Date().toLocaleDateString()}`);
        if (title) {
          try {
            const transcriptResult: any = {
              segments: [],
              fullText: transcriptText,
              duration: 0,
            };
            await saveTranscript(roomId || tabId || 'unknown', transcriptResult, {
              roomTitle: title,
              participants: participants.map(p => p.name),
            });
            console.log('[Mobile VR] Transcript saved:', title);
          } catch (error) {
            console.error('[Mobile VR] Failed to save transcript:', error);
          }
        }
      }
    }

    // Stop media
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }

    // Stop transcription if active
    if (isTranscribing) {
      await transcriptionService.stopRecording();
    }

    onClose();
  }, [isTranscribing, transcriptText, localStream, transcriptionService, onClose, roomId, tabId, tabLabel, participants]);

  const handleClose = useCallback(() => {
    // Show confirmation dialog
    setShowEndCallConfirm(true);
  }, []);

  // Toggle mic
  const toggleMic = useCallback(() => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !micOn;
      }
    }
    setMicOn(!micOn);
  }, [micOn, localStream]);

  // Toggle camera
  const toggleCamera = useCallback(() => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !cameraOn;
      }
    }
    setCameraOn(!cameraOn);
  }, [cameraOn, localStream]);

  // Snack Order Handlers
  const handleOrderClick = (restaurantName: string, itemName: string, url?: string) => {
    setPendingOrder({ restaurant: restaurantName, item: itemName, url });
    setShowETAModal(true);
  };

  const handleETAConfirm = (minutes: number, notes: string) => {
    if (!pendingOrder) return;

    const newOrder: ActiveOrder = {
      id: `order_${Date.now()}`,
      restaurantName: pendingOrder.restaurant,
      itemName: pendingOrder.item,
      orderedAt: new Date(),
      estimatedMinutes: minutes,
      notes: notes || undefined,
      url: pendingOrder.url,
      status: 'active',
    };

    setActiveOrders(prev => [...prev, newOrder]);
    setShowETAModal(false);
    setPendingOrder(null);

    console.log('[Mobile Snack] Order tracking started:', newOrder);
  };

  const handleOrderArrived = (orderId: string) => {
    setActiveOrders(prev => prev.map(order => 
      order.id === orderId ? { ...order, status: 'arrived' as const } : order
    ));
    console.log('[Mobile Snack] Order marked as arrived:', orderId);

    setTimeout(() => {
      setActiveOrders(prev => prev.filter(o => o.id !== orderId));
    }, 2000);
  };

  const handleOrderDelayed = (orderId: string) => {
    setActiveOrders(prev => prev.map(order => 
      order.id === orderId ? { ...order, status: 'delayed' as const } : order
    ));
    console.log('[Mobile Snack] Order marked as delayed:', orderId);
  };

  const handleCheckOrderStatus = (orderId: string, url: string) => {
    console.log('[Mobile Snack] Checking order status:', orderId);
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleEndCall = () => {
    sessionStorage.removeItem('whipt_on_call');
    sessionStorage.removeItem('whipt_call_start');
    sessionStorage.removeItem('whipt_call_spot');
    // Reset meeting data for fresh start
    setMessages([]);
    setAiChatHistory([]);
    setInputValue('');
    setTranscriptText('');
    setIsTranscribing(false);
    console.log('[MobileVirtualRoomNew] Meeting ended and reset');
  };

  // Start call
  const handleJoinCall = useCallback(async () => {
    console.log('[MobileVirtualRoomNew] Join Call clicked!');
    
    // Clear previous meeting data for fresh start
    resetMeeting();
    
    try {
      // Request media access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      console.log('[MobileVirtualRoomNew] Got media stream:', stream);
      setLocalStream(stream);
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      console.log('[MobileVirtualRoomNew] Setting onCall to true');
      setOnCall(true);
      setMicOn(true);
      setCameraOn(true);
    } catch (error) {
      console.error('[MobileVirtualRoomNew] Failed to get media devices:', error);
      // Join anyway without media
      console.log('[MobileVirtualRoomNew] Joining without media');
      setOnCall(true);
      setMicOn(false);
      setCameraOn(false);
    }
  }, [resetMeeting]);

  console.log('[MobileVirtualRoomNew] About to render. onCall:', onCall, 'micOn:', micOn, 'cameraOn:', cameraOn);
  console.log('[MobileVirtualRoomNew] Conditional check - will show:', !onCall ? 'Pre-call screen' : 'In-call view');

  // Picture-in-Picture minimized view (copy from desktop)
  if (minimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 w-64 bg-black/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-purple-500/30 overflow-hidden"
        style={{ maxHeight: '300px' }}
      >
        {/* PIP Header */}
        <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 px-3 py-2 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-xs font-bold text-white">
              {roomTitle.substring(0, 2).toUpperCase()}
            </div>
            <span className="text-xs font-semibold text-white truncate">{roomTitle}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onMinimize}
              className="w-6 h-6 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              title="Restore"
            >
              <Video className="h-3 w-3" />
            </button>
            <button
              onClick={() => setShowEndCallConfirm(true)}
              className="w-6 h-6 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 flex items-center justify-center transition-colors"
              title="End Call"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* PIP Video */}
        <div className="relative bg-black aspect-video">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          {!cameraOn && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#1f1b39] to-[#03030b]">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#ec4899] to-[#a855f7] flex items-center justify-center text-lg font-bold text-white shadow-2xl">
                {userInitials}
              </div>
            </div>
          )}
          
          {/* PIP Quick Controls */}
          <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-2">
            <button
              onClick={toggleMic}
              className={`w-7 h-7 rounded-full ${micOn ? 'bg-emerald-500' : 'bg-red-500/80'} backdrop-blur-sm flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform`}
            >
              {micOn ? <Mic className="h-3 w-3" /> : <MicOff className="h-3 w-3" />}
            </button>
            <button
              onClick={toggleCamera}
              className={`w-7 h-7 rounded-full ${cameraOn ? 'bg-emerald-500' : 'bg-red-500/80'} backdrop-blur-sm flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform`}
            >
              {cameraOn ? <Video className="h-3 w-3" /> : <VideoOff className="h-3 w-3" />}
            </button>
          </div>

          {/* On Call Indicator */}
          {onCall && callSpotName && (
            <div className="absolute top-2 left-2 right-2 bg-amber-500/90 backdrop-blur-sm px-2 py-1 rounded-lg">
              <p className="text-[10px] font-semibold text-white text-center">📞 {callSpotName}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-[#0a0612] to-[#1a0f2e] flex flex-col z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-b border-purple-500/30">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white text-sm font-bold">
            {tabLabel[0]}
          </div>
          <h2 className="text-white font-semibold text-base">{roomTitle}</h2>
        </div>
        
        <div className="flex items-center gap-2">
          {/* AI Toggle Button */}
          <button
            onClick={() => setAiEnabled(!aiEnabled)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              aiEnabled
                ? 'bg-green-500/30 text-green-200 border border-green-400/50'
                : 'bg-gray-500/30 text-gray-300 border border-gray-400/50'
            }`}
            title="Toggle AI Assistant"
          >
            AI {aiEnabled ? 'ON' : 'OFF'}
          </button>

          {/* Change Name */}
          <button
            onClick={() => {
              const newName = prompt('Enter new room name:', roomTitle);
              if (newName) {
                // TODO: Save new name
                console.log('New room name:', newName);
              }
            }}
            className="px-3 py-1.5 bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors rounded-lg text-xs font-semibold whitespace-nowrap"
            title="Change Name"
          >
            Rename
          </button>

          {/* Minimize */}
          <button
            onClick={() => {
              console.log('[Mobile VR] Minimize to floating PiP');
              if (onMinimize) {
                onMinimize(); // Minimize to floating video overlay
              } else {
                console.warn('[Mobile VR] No onMinimize handler - add to parent component');
              }
            }}
            className="p-2 rounded-lg bg-gray-500/20 text-gray-300 hover:bg-gray-500/30 transition-colors"
            title="Minimize to floating video"
          >
            <span className="text-sm">─</span>
          </button>

          {/* Transcript Status */}
          {isTranscribing && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-400/30 rounded-lg">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
              <span className="text-emerald-200 text-xs font-semibold">
                {transcriptText.length} chars
              </span>
            </div>
          )}
          
          {/* Transcript Ready */}
          {lastTranscript && !isTranscribing && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 border border-purple-400/30 rounded-lg">
              <span className="text-purple-200 text-xs font-semibold">Transcript ready</span>
            </div>
          )}
          
          {/* Close */}
          <button
            onClick={handleClose}
            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Content - Three Column Layout */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!onCall ? (
          /* Pre-call screen */
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                <Users className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-white text-xl font-semibold mb-2">{roomTitle}</h3>
              <p className="text-gray-400 mb-6">{description || 'Ready to join?'}</p>
              <button
                onClick={(e) => {
                  console.log('[MobileVirtualRoomNew] Button clicked!', e);
                  handleJoinCall();
                }}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all"
              >
                Join Call
              </button>
            </div>
          </div>
        ) : (
          /* In-call: WEBPAGE LAYOUT - Sidebar + Scrollable Main Content */
          <div className="flex h-full overflow-hidden">
            {/* LEFT SIDEBAR: Participant Thumbnails */}
            <div className="w-16 flex flex-col bg-gray-900/50 border-r border-gray-700 overflow-y-auto p-1 gap-1 flex-shrink-0">
              {participants.map((participant) => (
                <div
                  key={participant.id}
                  className="flex-shrink-0 aspect-square rounded-lg bg-gradient-to-br from-purple-600/20 to-pink-600/20 border border-purple-500/30 flex items-center justify-center relative"
                >
                  <div className="text-white text-[10px] font-bold">{participant.initials}</div>
                  {participant.online && (
                    <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  )}
                </div>
              ))}
            </div>

            {/* MAIN CONTENT AREA: Scrollable */}
            <div className="flex-1 flex flex-col overflow-y-auto pb-16">
              {/* Chat Messages - Fixed height wall */}
              <div className="p-2 bg-gray-900/30 border-b border-gray-700 flex-shrink-0">
                <div className="text-white text-xs font-semibold mb-2">💬 Chat</div>
                <div className="space-y-2 h-20 overflow-y-auto">
                  {messages.length === 0 ? (
                    <div className="text-center text-gray-500 text-xs py-2">No messages yet</div>
                  ) : (
                    messages.map((msg) => (
                      <div key={msg.id} className="flex gap-1 items-start">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0 ${
                          msg.sender === displayName ? 'bg-purple-600' : 'bg-gray-600'
                        }`}>
                          {msg.sender?.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className={`max-w-[70%] px-2 py-1 rounded text-xs ${
                          msg.sender === displayName ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-200'
                        }`}>
                          {msg.text}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* MAIN VIDEO - Zooms to thumbnail when keyboard visible */}
              <div className="flex-1 flex items-center justify-center p-2 bg-black">
                <div className={`relative aspect-video bg-black rounded-xl overflow-hidden border-2 border-purple-500/30 transition-all duration-300 ${
                  keyboardVisible 
                    ? 'w-32 h-20' 
                    : 'w-full max-w-2xl'
                }`}>
                  {cameraOn && localStream ? (
                    <video
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/30 to-pink-900/30">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-3xl font-bold">
                        {displayName.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                    </div>
                  )}
                  
                  {/* Name Label with Rename Button */}
                  <div className="absolute bottom-2 left-2 px-3 py-1 rounded-lg bg-black/70 backdrop-blur-sm text-white text-xs font-semibold flex items-center gap-2">
                    <span onClick={() => {
                      const newName = prompt('Enter your name:', displayName);
                      if (newName && newName.trim()) {
                        setDisplayName(newName.trim());
                      }
                    }} className="cursor-pointer hover:text-purple-400 transition-colors">
                      {displayName} (You)
                    </span>
                    {!micOn && <MicOff className="w-3 h-3 text-red-400" />}
                  </div>

                  {/* Live Captions Overlay - Glassmorphism */}
                  {isTranscribing && (
                    <div className="absolute bottom-2 left-2 right-2 px-3 py-2 rounded-lg bg-black/40 backdrop-blur-xl border border-purple-400/60 z-20 shadow-2xl">
                      <div className="flex items-start gap-2">
                        <span className="text-purple-400 text-xs font-bold flex-shrink-0">CC:</span>
                        <p className="text-purple-200 text-xs leading-relaxed">
                          {transcriptText || '🎤 Listening...'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* AI/Snacks Button OR Progress Bar */}
              <div className="p-2 bg-gray-900/30 border-t border-gray-700">
                {activeOrders.length > 0 ? (
                  /* Progress Bar for Active Orders */
                  <button
                    onClick={() => setShowSideTab(!showSideTab)}
                    className="w-full px-4 py-2 bg-gradient-to-r from-pink-600 to-orange-600 text-white rounded-lg hover:from-pink-700 hover:to-orange-700 transition-all overflow-hidden relative"
                  >
                    {/* Progress background */}
                    <div 
                      className="absolute inset-0 bg-green-500/30 transition-all duration-1000"
                      style={{ 
                        width: `${activeOrders[0]?.progress || 0}%`,
                        transition: 'width 1s linear'
                      }}
                    />
                    <div className="relative z-10 flex items-center justify-between text-sm font-semibold">
                      <span>📦 Tracking Order</span>
                      <span className="text-xs opacity-90">{activeOrders[0]?.eta || 'Calculating...'}</span>
                    </div>
                  </button>
                ) : (
                  /* Regular AI/Snacks Button */
                  <button
                    onClick={() => setShowSideTab(!showSideTab)}
                    className="w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-semibold flex items-center justify-center gap-2 text-sm"
                  >
                    <span>AI Assistant & Snacks</span>
                    <span className="text-xs opacity-80">{showSideTab ? '✕' : '→'}</span>
                  </button>
                )}
              </div>

              {/* Chat Input */}
              <div className="p-2 bg-gray-900/30 border-t border-gray-700">
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      const emoji = prompt('Enter emoji:') || '😊';
                      setInputValue(prev => prev + emoji);
                    }}
                    className="px-2 py-1 bg-gray-700 text-yellow-400 rounded hover:bg-gray-600 text-xs"
                  >
                    😊
                  </button>
                  <input
                    ref={inputRef as any}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type message..."
                    className="flex-1 px-2 py-1 bg-gray-800 text-white text-xs rounded border border-gray-700 focus:outline-none focus:border-purple-500"
                  />
                  <button
                    onClick={handleSendMessage}
                    className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1 text-xs font-semibold"
                  >
                    <Send className="w-3 h-3" />
                    Send
                  </button>
                </div>
              </div>

              {/* Agenda Only */}
              <div className="p-2 bg-gray-900/30 border-t border-gray-700">
                <div className="text-xs font-bold text-purple-400 mb-2">📋 Agenda</div>
                <div className="space-y-1">
                  {agenda.length === 0 ? (
                    <p className="text-[10px] text-gray-500 italic">No agenda items</p>
                  ) : (
                    agenda.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-[10px] text-gray-300">
                        <span className="text-purple-400">•</span>
                        <span>{item}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Removed Snack Tracker & AI Assistant - Now in side tab */}
            </div>
          </div>
        )}

        {/* SLIDE-OUT PANEL - Right Side */}
        {onCall && showSideTab && (
          <>
                {/* Overlay */}
                <div 
                  className="fixed inset-0 bg-black/50 z-40"
                  onClick={() => setShowSideTab(false)}
                />
                
                {/* Panel */}
                <div className="fixed right-0 top-0 bottom-0 w-80 backdrop-blur-xl bg-gray-900/80 z-50 shadow-2xl overflow-y-auto animate-slide-left border-l border-white/10">
                  {/* Panel Header */}
                  <div className="sticky top-0 bg-gradient-to-r from-purple-600/90 to-pink-600/90 backdrop-blur-sm px-4 py-3 flex items-center justify-between border-b border-white/20">
                    <h3 className="text-white font-bold">AI Assistant & Snacks</h3>
                    <button
                      onClick={() => setShowSideTab(false)}
                      className="text-white hover:bg-white/20 rounded p-1"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* AI Assistant Section */}
                  <div className="p-4 border-b border-white/10 bg-white/5 backdrop-blur-sm">
                    <h4 className="text-purple-400 font-semibold text-sm mb-3">AI Assistant</h4>
                    
                    {/* Q&A Display */}
                    {aiChatHistory.length > 0 && (
                      <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                        {aiChatHistory.slice(-5).map((qa, i) => (
                          <div key={i} className="p-2 bg-purple-900/20 rounded border border-purple-500/30">
                            <div className="text-xs text-green-400 font-semibold mb-1">Q: {qa.question}</div>
                            <div className="text-xs text-gray-300">{qa.answer}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Ask AI Input */}
                    <input
                      type="text"
                      placeholder={aiEnabled ? "Ask AI anything..." : "AI is OFF - toggle to enable"}
                      disabled={!aiEnabled}
                      onKeyPress={async (e) => {
                        if (e.key === 'Enter' && e.currentTarget.value.trim() && aiEnabled) {
                          const question = e.currentTarget.value.trim();
                          const input = e.currentTarget;
                          input.value = '';
                          
                          // Add question with loading state
                          setAiChatHistory(prev => [...prev, { 
                            question, 
                            answer: "🤔 Thinking..." 
                          }]);
                          
                          try {
                            // Simple AI response (you can connect to OpenAI API here)
                            const context = `Room: ${roomTitle}, Participants: ${participants.map(p => p.name).join(', ')}, Transcript: ${transcriptText.substring(0, 500)}`;
                            const answer = await generateAIResponse(question, context);
                            
                            // Update with real answer
                            setAiChatHistory(prev => {
                              const updated = [...prev];
                              updated[updated.length - 1].answer = answer;
                              return updated;
                            });
                          } catch (error) {
                            console.error('[AI] Error generating response:', error);
                            setAiChatHistory(prev => {
                              const updated = [...prev];
                              updated[updated.length - 1].answer = "⚠️ Could not generate response. Please try again.";
                              return updated;
                            });
                          }
                        }
                      }}
                      className={`w-full text-white text-sm rounded px-3 py-2 border focus:outline-none transition-all ${
                        aiEnabled 
                          ? 'bg-gray-800/80 border-purple-500/50 focus:border-purple-500' 
                          : 'bg-gray-700/50 border-gray-600 cursor-not-allowed opacity-60'
                      }`}
                    />
                  </div>

                  {/* Snack Tracker Section */}
                  {activeOrders.length > 0 && (
                    <div className="p-4 border-b border-white/10 bg-white/5 backdrop-blur-sm">
                      <h4 className="text-pink-400 font-semibold text-sm mb-3">Snack Orders</h4>
                      <SnackOrderTracker
                        orders={activeOrders}
                        onMarkArrived={handleOrderArrived}
                        onMarkDelayed={handleOrderDelayed}
                        onCheckStatus={handleCheckOrderStatus}
                        onOpenSnackPanel={() => setShowSnackPanel(true)}
                      />
                    </div>
                  )}
                  
                  {/* Open Snacks Button */}
                  <div className="p-4">
                    <button
                      onClick={() => {
                        setShowSnackPanel(true);
                        setShowSideTab(false);
                      }}
                      className="w-full px-4 py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg hover:from-pink-700 hover:to-purple-700 transition-all font-semibold flex items-center justify-center gap-2"
                    >
                      <Utensils className="w-5 h-5" />
                      Order Snacks
                    </button>
                  </div>
                </div>
              </>
            )}

        {/* Bottom Control Bar - Always visible at bottom */}
        {onCall && (
          <div className="absolute bottom-0 left-0 right-0 px-2 py-2 bg-gradient-to-r from-gray-900 to-gray-800 border-t border-gray-700 flex items-center justify-center gap-2">
            <button
                onClick={toggleMic}
                className={`p-2 rounded-full transition-colors ${
                  micOn ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}
                title="Mic"
              >
                {micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
              </button>

              <button
                onClick={toggleCamera}
                className={`p-2 rounded-full transition-colors ${
                  cameraOn ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}
                title="Video"
              >
                {cameraOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
              </button>

              <button
                onClick={toggleTranscription}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg font-semibold transition-all text-xs ${
                  isTranscribing
                    ? 'bg-emerald-500/30 text-emerald-100 border border-emerald-300/40'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                title="AI Transcription"
              >
                {isTranscribing ? "⏹ CC" : "💬 CC"}
              </button>

              <button
                onClick={() => setShowSnackPanel(!showSnackPanel)}
                className="flex items-center gap-1 px-3 py-2 rounded-lg bg-pink-500/20 text-pink-200 hover:bg-pink-500/30 transition-all text-xs"
                title="Snacks"
              >
                <Utensils className="w-4 h-4" />
                <span>Snacks</span>
              </button>
            </div>
        )}
      </div>

      {/* Snack Panel - Slide in from bottom */}
      {showSnackPanel && (
        <div 
          className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-end"
          onClick={() => setShowSnackPanel(false)}
        >
          <div 
            className="w-full max-h-[85vh] overflow-y-auto bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-t-3xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-br from-[#1a1a2e] to-[#16213e] p-4 flex items-center justify-between border-b border-white/10">
              <h2 className="text-lg font-bold text-white">🍕 Late Night Snacks</h2>
              <button
                onClick={() => setShowSnackPanel(false)}
                className="w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <LateNightSnackPanel
                spots={snackSpots}
                loading={snackLoading}
                onCreate={onCreateSnack || (async () => {})}
                onUpdate={onUpdateSnack || (async () => {})}
                onDelete={onDeleteSnack || (async () => {})}
                onMarkOrdered={onMarkSnackOrdered || (async () => {})}
                onOrderClick={handleOrderClick}
              />
            </div>
          </div>
        </div>
      )}

      {/* Order ETA Modal */}
      {pendingOrder && (
        <OrderETAModal
          isOpen={showETAModal}
          restaurantName={pendingOrder.restaurant}
          itemName={pendingOrder.item}
          onConfirm={handleETAConfirm}
          onCancel={() => {
            setShowETAModal(false);
            setPendingOrder(null);
          }}
        />
      )}

      {/* End Call Confirmation Dialog */}
      {showEndCallConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-red-500/50 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <PhoneOff className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white">End Meeting?</h3>
            </div>
            <p className="text-gray-300 text-sm mb-6">
              Are you sure you want to end this meeting? This will disconnect you from the virtual room.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndCallConfirm(false)}
                className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowEndCallConfirm(false);
                  confirmAndClose();
                }}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors"
              >
                End Meeting
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileVirtualRoomNew;
